import { useEffect, useState } from "react";
import { getFacilityKeyForPortal } from "@/store/profile";
import authRequest from "@/api/auth-request";
import type { PatientFacilityInfo } from "@/api/auth-request";
import { formatPhone } from "@/utils/format";
import { cn } from "@/utils/cn";

export interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

export function ContactModal({ open, onClose }: ContactModalProps) {
  const [facilityInfo, setFacilityInfo] = useState<PatientFacilityInfo | null>(
    null
  );

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const key = getFacilityKeyForPortal();
    if (!key) return;
    let cancelled = false;
    authRequest.request
      .getFacilityInfo(key)
      .then((res) => {
        if (!cancelled && res?.data) setFacilityInfo(res.data);
      })
      .catch(() => setFacilityInfo(null));
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-[max(1rem,env(safe-area-inset-top,0px))]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        aria-hidden
      />
      <div
        className={cn(
          "relative max-h-[90dvh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-2xl border border-border bg-white p-6 shadow-xl sm:p-8"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4">
          <h2
            id="contact-modal-title"
            className="text-xl font-semibold text-foreground"
          >
            Contact Us
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-healthsource-navy"
            aria-label="Close"
          >
            <i className="fas fa-times text-lg" aria-hidden />
          </button>
        </div>
        {!facilityInfo && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Loading contact information…
          </p>
        )}
        {facilityInfo && (
          <dl className="mt-4 space-y-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Office Name
              </dt>
              <dd className="mt-0.5 text-foreground">
                {facilityInfo.officeName}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Address
              </dt>
              <dd className="mt-0.5 text-foreground">
                {facilityInfo.address}
                {facilityInfo.address2 && (
                  <span className="block">{facilityInfo.address2}</span>
                )}
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-0">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  City
                </dt>
                <dd className="mt-0.5 text-foreground">{facilityInfo.city}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  State
                </dt>
                <dd className="mt-0.5 text-foreground">
                  {facilityInfo.state}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Zip
                </dt>
                <dd className="mt-0.5 text-foreground">{facilityInfo.zip}</dd>
              </div>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Phone
              </dt>
              <dd className="mt-0.5">
                <a
                  href={`tel:${facilityInfo.phone?.replace(/\D/g, "")}`}
                  className="inline-flex min-h-11 items-center text-healthsource-navy underline decoration-healthsource-navy/50 underline-offset-2 hover:decoration-healthsource-navy"
                >
                  {formatPhone(facilityInfo.phone)}
                </a>
              </dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}
